import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import './BlockManagement.css';

const BlockManagement = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  
  // Form states
  const [newBlockSerial, setNewBlockSerial] = useState('');
  const [newBlockName, setNewBlockName] = useState('');
  const [testTemp, setTestTemp] = useState('');
  const [testHumidity, setTestHumidity] = useState('');
  const [testDispenseSize, setTestDispenseSize] = useState('');
  const [testDispensePrice, setTestDispensePrice] = useState('');
  const [testDispenseType, setTestDispenseType] = useState('regular');
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState('');

  const functions = getFunctions();
  const createBlock = httpsCallable(functions, 'createBlock');
  const logTemp = httpsCallable(functions, 'logTemp');
  const logDispense = httpsCallable(functions, 'logDispense');
  const getBlockStats = httpsCallable(functions, 'getBlockStats');
  const checkTempCompliance = httpsCallable(functions, 'checkTempCompliance');

  const fetchBlocks = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'blocks'));
      const blocksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBlocks(blocksData);
      setAvailableBlocks(blocksData.map(block => ({
        serial: block.serial,
        name: block.name
      })));
    } catch (err) {
      setError('Error fetching blocks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        "https://us-central1-blockautomation-ac669.cloudfunctions.net/createBlock",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serial: newBlockSerial,
            name: newBlockName || undefined,
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log("Create block result:", result);
      await fetchBlocks();
      setNewBlockSerial("");
      setNewBlockName("");
    } catch (err) {
      setError("Error creating block: " + err.message);
    }
  };

  const handleLogTemp = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        "https://us-central1-blockautomation-ac669.cloudfunctions.net/logTemp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serial: selectedBlock,
            temperature: parseFloat(testTemp),
            humidity: parseFloat(testHumidity),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to log temperature");
      }

      await fetchBlocks();
      setTestTemp("");
      setTestHumidity("");
    } catch (err) {
      setError("Error logging temperature: " + err.message);
    }
  };

  const handleLogDispense = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        "https://us-central1-blockautomation-ac669.cloudfunctions.net/logDispense",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serial: selectedBlock,
            size: parseFloat(testDispenseSize),
            price: parseFloat(testDispensePrice),
            type: testDispenseType
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to log dispense");
      }

      await fetchBlocks();
      setTestDispenseSize("");
      setTestDispensePrice("");
    } catch (err) {
      setError("Error logging dispense: " + err.message);
    }
  };

  const handleDelete = (blockId) => {
    setBlockToDelete(blockId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDoc(doc(db, 'blocks', blockToDelete));
      await deleteDoc(doc(db, 'block_data', blockToDelete));
      setBlocks(blocks.filter(block => block.id !== blockToDelete));
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error deleting block: ' + err.message);
    }
  };

  const formatTemperature = (temp) => {
    // Extract the numeric value and format to one decimal place
    const numericValue = parseFloat(temp);
    return isNaN(numericValue) ? temp : `${numericValue.toFixed(1)}°F`;
  };

  if (loading) {
    return <div className="loading">Loading blocks data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="block-management">
      <h1 className="page-title">Block Management</h1>
      <div className="test-controls">
        <form onSubmit={handleCreateBlock} className="test-form">
          <h3>Create New Block</h3>
          <input
            type="text"
            placeholder="Serial Number"
            value={newBlockSerial}
            onChange={(e) => setNewBlockSerial(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Block Name (optional)"
            value={newBlockName}
            onChange={(e) => setNewBlockName(e.target.value)}
          />
          <button type="submit">Create Block</button>
        </form>

        <form onSubmit={handleLogTemp} className="test-form">
          <h3>Log Temperature</h3>
          <select
            value={selectedBlock}
            onChange={(e) => setSelectedBlock(e.target.value)}
            required
          >
            <option value="">Select a Block</option>
            {availableBlocks.map(block => (
              <option key={block.serial} value={block.serial}>
                {block.name} ({block.serial})
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Temperature (°F)"
            value={testTemp}
            onChange={(e) => setTestTemp(e.target.value)}
            required
          />
          <input
            type="number"
            placeholder="Humidity (%)"
            value={testHumidity}
            onChange={(e) => setTestHumidity(e.target.value)}
            required
          />
          <button type="submit">Log Temperature</button>
        </form>

        <form onSubmit={handleLogDispense} className="test-form">
          <h3>Log Dispense</h3>
          <select
            value={selectedBlock}
            onChange={(e) => setSelectedBlock(e.target.value)}
            required
          >
            <option value="">Select a Block</option>
            {availableBlocks.map(block => (
              <option key={block.serial} value={block.serial}>
                {block.name} ({block.serial})
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Size (oz)"
            value={testDispenseSize}
            onChange={(e) => setTestDispenseSize(e.target.value)}
            required
          />
          <input
            type="number"
            placeholder="Price ($)"
            value={testDispensePrice}
            onChange={(e) => setTestDispensePrice(e.target.value)}
            required
          />
          <select
            value={testDispenseType}
            onChange={(e) => setTestDispenseType(e.target.value)}
            required
          >
            <option value="regular">Regular</option>
            <option value="premium">Premium</option>
          </select>
          <button type="submit">Log Dispense</button>
        </form>
      </div>

      {blocks.length === 0 ? (
        <p>No blocks data available</p>
      ) : (
        <table className="blocks-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Temperature</th>
              <th>Last Checked</th>
              <th>Dispenses Today</th>
              <th>Dispenses Week</th>
              <th>Dispenses Lifetime</th>
              <th>Temperature Band</th>
              <th>Fill Level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blocks.map((block) => (
              <tr key={block.id}>
                <td>{block.name}</td>
                <td>{block.status}</td>
                <td>{formatTemperature(block.temperature)}</td>
                <td>{new Date(block.lastChecked).toLocaleString()}</td>
                <td className="dispenses-cell">{block.dispensesToday}</td>
                <td className="dispenses-cell">{block.dispensesWeek}</td>
                <td className="dispenses-cell">{block.dispensesLifetime}</td>
                <td className={`temp-band ${block.inTempBand ? 'in-band' : 'out-band'}`}>
                  {block.inTempBand ? '✓ In Band' : '⚠ Out of Band'}
                </td>
                <td>{block.fill_level !== undefined ? block.fill_level + '%' : 'N/A'}</td>
                <td>
                  <button 
                    onClick={() => handleDelete(block.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showDeleteConfirm && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this block?</p>
            <div className="delete-modal-buttons">
              <button onClick={confirmDelete}>Yes, Delete</button>
              <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockManagement;